/**
 * /operations/transactions/leave/new — Leave HR-initiate form
 * (M30.5.3). Composes EmployeePicker + reason-code dropdown
 * (YAML-sourced) + leave-type-specific fields + the 5 AI assist
 * panels via TransactionFormShell.
 *
 * Reason codes come from `api.listReasonCodes('leave')` — Amer's
 * hard constraint: never hardcode strings in TSX.
 */

import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { EmployeePicker } from "../../components/ui/EmployeePicker";
import { TransactionFormShell } from "../../components/hr_operations";
import { api, type EmployeePickerOption } from "../../lib/api";
import { useHrActor } from "../../lib/use-hr-actor";
import { useTransactionAssist } from "../../lib/use-transaction-assist";

export function LeaveHrInitiatePage() {
  const { t, i18n } = useTranslation();
  const isAr = i18n.resolvedLanguage === "ar";
  const navigate = useNavigate();

  // Form fields
  const [employee, setEmployee] = useState<EmployeePickerOption | null>(
    null,
  );
  const [reasonCode, setReasonCode] = useState<string>("");
  const [explanation, setExplanation] = useState<string>("");
  const [docUrl, setDocUrl] = useState<string>("");
  const [leaveTypeCode, setLeaveTypeCode] = useState<string>("annual");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [unpaidOverride, setUnpaidOverride] = useState<boolean>(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Reason codes from YAML loader.
  const reasonCodes = useQuery({
    queryKey: ["reason-codes", "leave"],
    queryFn: () => api.listReasonCodes("leave"),
    staleTime: 5 * 60_000,
  });

  // Actor identity from Clerk (audit fix C1 — commandment 39).
  const actor = useHrActor();

  // Build the assist body — only fire when prerequisites are set
  // AND Clerk has resolved the user.
  const enabled =
    actor.isReady &&
    !!employee &&
    !!reasonCode &&
    !!startDate &&
    !!endDate;
  const body = {
    actor_user_id: actor.actorUserId,
    actor_role: actor.actorRole,
    target_employee_id: employee?.id ?? "",
    reason_code: reasonCode,
    reason_explanation: explanation || null,
    supporting_document_url: docUrl || null,
    leave_type_code: leaveTypeCode,
    start_date: startDate,
    end_date: endDate,
    is_unpaid_override: unpaidOverride || null,
  };
  const {
    result,
    isLoading: assistLoading,
    error: assistError,
    acknowledgedCodes,
    acknowledge,
  } = useTransactionAssist({
    body,
    enabled,
    callAssist: (b) => api.leaveHrInitiateAssist(b),
  });

  async function submit() {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await api.leaveHrInitiateCommit({
        ...body,
        ai_findings_acknowledged: Array.from(acknowledgedCodes),
      });
      setSubmitSuccess(true);
      // After a tick, redirect to the payroll-candidates queue
      // so HR sees the impact of their submission.
      setTimeout(
        () => navigate("/operations/payroll-candidates"),
        1500,
      );
    } catch (e) {
      setSubmitError(String(e));
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleAcceptSuggestion(fieldRef: string, value: unknown) {
    if (fieldRef === "reason_explanation" && typeof value === "string") {
      setExplanation(value);
    }
  }

  return (
    <TransactionFormShell
      transactionType="leave"
      title={t("operations.leave_form_title")}
      assist={result}
      isAssistLoading={assistLoading}
      assistError={assistError}
      acknowledgedCodes={acknowledgedCodes}
      onAcknowledge={acknowledge}
      onAcceptSuggestion={handleAcceptSuggestion}
      onSubmit={submit}
      isSubmitting={isSubmitting || !actor.isReady}
      submitSuccess={submitSuccess}
      submitError={submitError}
    >
      <Field label={t("operations.form_employee_label")}>
        <EmployeePicker
          value={employee?.id ?? null}
          onChange={setEmployee}
          selected={employee}
        />
      </Field>

      <Field label={t("operations.form_reason_code_label")}>
        <select
          className="w-full rounded-md border border-border-default bg-surface-default px-3 py-2 text-sm text-text-primary"
          value={reasonCode}
          onChange={(e) => setReasonCode(e.target.value)}
        >
          <option value="">—</option>
          {reasonCodes.data?.codes.map((c) => (
            <option key={c.code} value={c.code}>
              {isAr ? c.display_ar : c.display_en}
            </option>
          ))}
        </select>
      </Field>

      <Field label={t("operations.leave_form_leave_type")}>
        <input
          type="text"
          className="w-full rounded-md border border-border-default bg-surface-default px-3 py-2 text-sm text-text-primary"
          value={leaveTypeCode}
          onChange={(e) => setLeaveTypeCode(e.target.value)}
          placeholder="annual"
          dir="ltr"
        />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label={t("operations.leave_form_start_date")}>
          <input
            type="date"
            className="w-full rounded-md border border-border-default bg-surface-default px-3 py-2 text-sm text-text-primary"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </Field>
        <Field label={t("operations.leave_form_end_date")}>
          <input
            type="date"
            className="w-full rounded-md border border-border-default bg-surface-default px-3 py-2 text-sm text-text-primary"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </Field>
      </div>

      <Field label={t("operations.form_reason_explanation_label")}>
        <textarea
          className="w-full rounded-md border border-border-default bg-surface-default px-3 py-2 text-sm text-text-primary"
          rows={3}
          value={explanation}
          onChange={(e) => setExplanation(e.target.value)}
        />
      </Field>

      <Field label={t("operations.form_supporting_doc_label")}>
        <input
          type="url"
          className="w-full rounded-md border border-border-default bg-surface-default px-3 py-2 text-sm text-text-primary"
          value={docUrl}
          onChange={(e) => setDocUrl(e.target.value)}
          placeholder={t("operations.form_supporting_doc_placeholder")}
          dir="ltr"
        />
      </Field>

      <label className="flex items-center gap-2 text-sm text-text-secondary">
        <input
          type="checkbox"
          checked={unpaidOverride}
          onChange={(e) => setUnpaidOverride(e.target.checked)}
        />
        {t("operations.leave_form_unpaid_override")}
      </label>
    </TransactionFormShell>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-text-secondary">
        {label}
      </span>
      {children}
    </label>
  );
}
